import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../auth/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';

// ============================================================
// VALIDATION HELPERS
// ============================================================
const validateEmail = (value) => {
  if (!value.trim()) return 'Email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address.';
  return '';
};

const validatePassword = (value) => {
  if (!value) return 'Password is required.';
  return '';
};

// ============================================================
// LOGIN COMPONENT
// ============================================================
export default function Login() {
  const navigate = useNavigate();
  const { completeAuthentication } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // --- Handle input changes ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear server error when user types
    if (serverError) setServerError('');
    // Live validation if field was touched
    if (touched[name]) {
      const err = name === 'email' ? validateEmail(value) : validatePassword(value);
      setErrors((prev) => ({ ...prev, [name]: err }));
    }
  };

  // --- Mark field as touched (on blur) ---
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = name === 'email' ? validateEmail(value) : validatePassword(value);
    setErrors((prev) => ({ ...prev, [name]: err }));
  };

  // --- Validate all fields ---
  const validateAll = () => {
    const newErrors = {
      email: validateEmail(form.email),
      password: validatePassword(form.password),
    };
    setErrors(newErrors);
    setTouched({ email: true, password: true });
    return !Object.values(newErrors).some(Boolean);
  };

  // --- Submit handler ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validateAll()) return;

    setLoading(true);
    try {
      const res = await api.auth.login({
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });

      const data = res.data?.data || {};
      await completeAuthentication(data);
      if (data.securityQuestionsPromptRequired) {
        navigate('/security-questions?mode=prompt', { replace: true, state: { required: false } });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      const payload = err.response?.data || {};
      if (payload.code === 'EMAIL_VERIFICATION_REQUIRED' && payload.data?.challengeId) {
        navigate('/verify-email?challengeId=' + encodeURIComponent(payload.data.challengeId), {
          replace: true,
          state: { email: form.email.trim().toLowerCase(), expiresAt: payload.data.expiresAt || null },
        });
        return;
      }
      const msg = payload.message || 'Login failed. Please try again.';
      setServerError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // STYLES (matching existing dark theme)
  // ============================================================
  const colors = {
    bg: '#0a0a0a',
    card: '#111111',
    inputBg: '#1a1a1a',
    border: '#2a2a2a',
    borderFocus: '#00d4aa',
    primary: '#00d4aa',
    primaryHover: '#00b892',
    error: '#ff4d4d',
    errorBg: 'rgba(255, 77, 77, 0.1)',
    textPrimary: '#ffffff',
    textSecondary: '#888888',
  };

  const inputBase = {
    padding: '14px 16px',
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.textPrimary,
    fontSize: '15px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: 'inherit',
  };

  return (
    <div style={{
      maxWidth: '440px',
      margin: '0 auto',
      padding: '40px 20px 60px',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: colors.textPrimary,
          marginBottom: '6px',
        }}>
          Welcome Back
        </h1>
        <p style={{ fontSize: '14px', color: colors.textSecondary }}>
          Sign in to your 9jaWin account
        </p>
      </div>

      {/* Server Error Banner */}
      {serverError && (
        <div style={{
          background: colors.errorBg,
          border: `1px solid ${colors.error}`,
          color: colors.error,
          padding: '12px 16px',
          borderRadius: '10px',
          marginBottom: '20px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span>⚠️</span> {serverError}
        </div>
      )}

      {/* Email/password form */}
      <form onSubmit={handleSubmit} noValidate>
        {/* Email */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{
            display: 'block',
            marginBottom: '6px',
            fontSize: '13px',
            fontWeight: '600',
            color: colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Email
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
            style={{
              ...inputBase,
              borderColor: touched.email && errors.email ? colors.error : colors.border,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = touched.email && errors.email ? colors.error : colors.borderFocus;
              e.target.style.boxShadow = `0 0 0 3px ${touched.email && errors.email ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
              handleBlur(e);
            }}
          />
          <div style={{ fontSize: '12px', color: colors.error, marginTop: '4px', minHeight: '16px' }}>
            {touched.email && errors.email}
          </div>
        </div>

        {/* Password */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            marginBottom: '6px',
            fontSize: '13px',
            fontWeight: '600',
            color: colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Password
          </label>
          <div style={{ position: 'relative' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={form.password}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your password"
            autoComplete="current-password"
            style={{
              ...inputBase, paddingRight: '54px',
              borderColor: touched.password && errors.password ? colors.error : colors.border,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = touched.password && errors.password ? colors.error : colors.borderFocus;
              e.target.style.boxShadow = `0 0 0 3px ${touched.password && errors.password ? 'rgba(255,77,77,0.15)' : 'rgba(0,212,170,0.15)'}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
              handleBlur(e);
            }}
          />
          <button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: colors.primary, cursor: 'pointer', padding: '6px', fontSize: '12px', fontWeight: '600' }}>
            {showPassword ? 'Hide' : 'Show'}
          </button>
          </div>
          <div style={{ fontSize: '12px', color: colors.error, marginTop: '4px', minHeight: '16px' }}>
            {touched.password && errors.password}
          </div>
        </div>

        {/* Forgot Password Link */}
        <div style={{ textAlign: 'right', marginBottom: '20px' }}>
          <Link to="/forgot-password" style={{
            fontSize: '13px',
            color: '#888',
            textDecoration: 'none',
            transition: 'color 0.2s',
          }}>
            Forgot Password?
          </Link>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '10px',
            border: 'none',
            background: loading ? '#00b892' : colors.primary,
            color: '#000',
            fontSize: '16px',
            fontWeight: '700',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'background 0.2s, opacity 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          {loading ? (
            <>
              Signing in...
            </>
          ) : (
            'Login'
          )}
        </button>
      </form>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0 4px', color: colors.textSecondary, fontSize: 12 }}>
        <span style={{ flex: 1, height: 1, background: colors.border }} />
        OR
        <span style={{ flex: 1, height: 1, background: colors.border }} />
      </div>

      <GoogleSignInButton
        mode="login"
        onSuccess={(data) => {
          completeAuthentication(data);
          navigate('/', { replace: true });
        }}
        onError={(message) => setServerError(message)}
      />

      {/* Register Link */}
      <p style={{
        textAlign: 'center',
        marginTop: '24px',
        fontSize: '14px',
        color: colors.textSecondary,
      }}>
        Don't have an account?{' '}
        <Link to="/register" style={{ color: colors.primary, fontWeight: '600', textDecoration: 'none' }}>
          Create Account
        </Link>
      </p>
    </div>
  );
}
